<template>
    <div>
        <v-card>
            <v-card-title>Matching Criteria</v-card-title>
            <v-card-subtitle><v-icon small>mdi-alert-circle</v-icon> Base Criteria for matching is limited to First Year Students and Alumni Court, Townhouses and Wemple buildings only.</v-card-subtitle>
            <v-card-text>
                <v-row>
                    <v-col cols="12" class="text-body-1">
                        <b>Residence Term</b>
                    </v-col>
                    <v-col cols="12">
                        <v-select v-model="term" class="pt-3" :items="residenceTerm" label="Residence Term"
                            placeholder="Please select.." prepend-icon="mdi-calendar-range" item-text="term" item-value="term" outlined hide-details dense :readonly="readonly">
                        </v-select>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" class="text-body-1">
                        <b>How is Matching &amp; Placement done?</b>
                    </v-col>
                    <v-col cols="12">
                        <v-timeline align-top dense>
                            <v-timeline-item icon="mdi-numeric-1" color="#0074B7">
                                <v-card>
                                    <v-card-title class="text-h6">
                                        Stage 1 - Reciprocal Matches
                                    </v-card-title>
                                    <v-card-text>
                                        The first stage seeks all applicants that have requested a roommate by name. 
                                        If there is a reciprocal match &#40;e.g. the named roommate also requested the applicant by name&#41;, the match is considered a success.
                                        <b>A reciprocal match requires a 100% match for the names specified.</b>
                                        It is possible that an applicant may not note the name of their requested roommate in the same way that person has named themselves on their ORA application. 
                                        <br /><br />For example, consider "Henry Jones" &#40;applicant&#41;; when when his friend March applied, his roommate request named him as "Henry 'Indiana' Jones". 
                                        Although we semantically understand that this is likely to be the same person, this may be an exception as opposed to a rule. 
                                        In testing we have seen examples in which a Canadian name would be noted as a "middle name" which broke a possible roommate name match.  <br /><br />
                                        <i>Unmatched reciprocals are listed in the generated roommate matcher report</i>, so it will be possible for you to make an assessment if any of those listed are likely matches.
                                    </v-card-text>
                                    <br />
                                </v-card>
                            </v-timeline-item>
                            <v-timeline-item icon="mdi-numeric-2" color="#0074B7">
                                <v-card>
                                    <v-card-title class="text-h6">
                                        Stage 2 - Building Placement
                                    </v-card-title>
                                    <v-card-text>
                                        The second stage sorts all applicants on a first-come, first-served basis. This is listed in the roommate matcher report as the "fcfsIndex". 
                                        After this sorting, the system first attempts to place the applicant in their first choice building. 
                                        If that placement cannot be made because of gender quota or if there is no longer available occupancy, an attempt will be made to place the applicant in their second choice building. 
                                        <br /><br /><b>Building occupancy limits are set for each building in Res-App &#62; Admin &#62; System Configuration &#62; Buildings.</b> <br /><br />Reciprocal matches are automatically placed together in this stage.<br /><br />
                                        <v-icon>mdi-information-outline</v-icon>Important Note: gender quotas have been statically set for Alumni Court as Females 66 / Males 49. Should this ratio need to be changed, please let ITS know and we will update as requested.
                                    </v-card-text>
                                    <br />
                                </v-card>
                            </v-timeline-item>
                            <v-timeline-item icon="mdi-numeric-3" color="#0074B7">
                                <v-card>
                                    <v-card-title class="text-h6">
                                        Stage 3 - Roommate Pairing by Criteria
                                    </v-card-title>
                                    <v-card-text>
                                        The third stage processes all unpaired applicants for each building. Each applicant in a building is compared to the others and criteria match scores are calculated. The best scored pairs are placed together. <br />
                                        <br />Scoring is determined as follows: <br />
                                        <ul>
                                            <li>Compatible Neatness &#40;must be +/- 2 grades&#41;</li>
                                            <li>Lifestyle Preferences - scores are determined by most common matches
                                                <ul>
                                                    <li>the following lifestyle criteria <i>prevent</i> a pairing:
                                                        <ul>
                                                            <li>"Any kind of noise distracts me while im studying" and "I like to study with background noise"</li>
                                                            <li>"I want my room to be a gathering place for friends" and "I prefer not to have many visitors in my room"</li>
                                                        </ul>
                                                    </li>
                                                </ul>
                                            </li>
                                            <li>Common Interests - scores are determined by most common matches from a list of interests provided which includes the following:
                                                <v-row>
                                                    <v-col cols="3">
                                                        <ul>
                                                            <li>"sports"</li>
                                                            <li>"video games"</li>
                                                            <li>"quiet"</li>
                                                            <li>"partying"</li>
                                                            <li>"movies"</li>
                                                        </ul>
                                                    </v-col>
                                                    <v-col cols="3">
                                                        <ul>
                                                            <li>"friends"</li>
                                                            <li>"dance"</li>
                                                            <li>"anime"</li>
                                                            <li>"cooking"</li>
                                                        </ul>
                                                    </v-col>
                                                    <v-col cols="3">
                                                        <ul>
                                                            <li>"baking"</li>
                                                            <li>"music"</li>
                                                            <li>"reading"</li>
                                                            <li>"martial arts"</li>
                                                        </ul>
                                                    </v-col>
                                                    <v-col cols="3">
                                                        <ul>
                                                            <li>"academics"</li>
                                                            <li>"food"</li>
                                                            <li>"fishing"</li>
                                                            <li>"hunting"</li>
                                                        </ul>
                                                    </v-col>
                                                </v-row>
                                            </li>
                                        </ul>
                                    </v-card-text><br />
                                </v-card>
                            </v-timeline-item>
                        </v-timeline>
                    </v-col>
                    <v-col cols="12">
                        After all stages are completed, the system will prompt for a save location for the generated report. 
                        All paired applicants &#40;reciprocal, criteria&#41; share a unique Pairing Code. <br /><br />
                        <b>If an applicant does not have a Pairing Code in the report, this reflects that a pairing could not be made because criteria was not satisfied </b>, and will be located at the end of each building's list.
                    </v-col>
                </v-row>
                <!--Filtering Criteria - might be needed for future update
                <v-row>
                    <v-col cols="12" class="text-body-1">
                        <b>Building</b>
                    </v-col>
                    <v-col cols="12">
                        <v-row>
                            <v-col cols="4">
                                <v-checkbox v-model="building" hide-details label="Alumni Court" value="Alumni Court" multiple dense :readonly="readonly"></v-checkbox>
                            </v-col>
                            <v-col cols="4">
                                <v-checkbox v-model="building" hide-details label="Townhouses" value="Townhouses" multiple dense :readonly="readonly"></v-checkbox>
                            </v-col>
                            <v-col cols="4">
                                <v-checkbox v-model="building" hide-details label="Monsignor Wemple" value="Monsignor Wemple" multiple dense :readonly="readonly"></v-checkbox>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" class="text-body-1">
                        <b>Lifestyle to be prioritized</b>
                    </v-col>
                    <v-col cols="12">
                        <v-row>
                            <v-col cols="12">
                                <v-checkbox v-model="priorityLifestyle" dense hide-details
                                    label="I expect to get up before 9AM on weekdays" multiple
                                    value="I expect to get up before 9AM on weekdays" :readonly="readonly">
                                </v-checkbox>
                                <v-checkbox v-model="priorityLifestyle" dense hide-details
                                    label="I usually stay up past midnight" multiple
                                    value="I usually stay up past midnight" :readonly="readonly">
                                </v-checkbox>
                                <v-checkbox v-model="priorityLifestyle" dense hide-details
                                    label="Any kind of noise distracts me while im studying" multiple
                                    value="Any kind of noise distracts me while im studying" :readonly="readonly">
                                </v-checkbox>
                                <v-checkbox v-model="priorityLifestyle" dense hide-details
                                    label="I like to study with background noise"
                                    value="I like to study with background noise" :readonly="readonly">
                                </v-checkbox>
                                <v-checkbox v-model="priorityLifestyle" dense hide-details
                                    label="I want my room to be a gathering place for friends" multiple
                                    value="I want my room to be a gathering place for friends" :readonly="readonly">
                                </v-checkbox>
                                <v-checkbox v-model="priorityLifestyle" dense hide-details
                                    label="I prefer not to have many visitors in my room" multiple
                                    value="I prefer not to have many visitors in my room" :readonly="readonly">
                                </v-checkbox>
                                <v-checkbox v-model="priorityLifestyle" dense hide-details
                                    label="I plan to maintain a fitness program" multiple
                                    value="I plan to maintain a fitness program" :readonly="readonly">
                                </v-checkbox>
                                <v-checkbox v-model="priorityLifestyle" dense hide-details
                                    label="I plan to tryout for a visitor sports team" multiple
                                    value="I plan to tryout for a visitor sports team" :readonly="readonly">
                                </v-checkbox>
                                <v-checkbox v-model="priorityLifestyle" dense hide-details
                                    label="I have toured/visited the residences at King's Unversity College" multiple
                                    value="I have toured/visited the residences at King's Unversity College" :readonly="readonly">
                                </v-checkbox>
                                <v-checkbox v-model="priorityLifestyle" dense hide-details
                                    label="I would like to be involved in the Residence Council or King's University College Student Council"
                                    multiple
                                    value="I would like to be involved in the Residence Council or King's University College Student Council" :readonly="readonly">
                                </v-checkbox>
                                <v-checkbox v-model="priorityLifestyle" dense hide-details
                                    label="I am a smoker (Please be advised that smoking is NOT permitted in any residence building)"
                                    multiple value="I am a smoker" :readonly="readonly">
                                </v-checkbox>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
                -->
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="#0A7029" :loading="matchingloading" @click="matchRoomates" :disabled="readonly" class="white--text">Match!</v-btn>
            </v-card-actions>
            <!-- Not needed as the results are not saved into a collection
            <v-divider></v-divider>
            <v-card-title>Download</v-card-title> 
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="#0A7029" @click="showmatchingresult = false" class="white--text">
                    Download
                </v-btn>
            </v-card-actions>
            -->
        </v-card>
        <v-dialog v-model="showmatchingresult" persistent scrollable>
            <v-card>
                <v-toolbar color="#0A7029" dense elevation="0" dark>
                    <v-toolbar-title>Roommate Matching Result</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn icon @click="showmatchingresult = false"><v-icon>mdi-close-thick</v-icon></v-btn>
                </v-toolbar><br />
                <v-card-text>
                    <v-simple-table dense style="border: 1px solid;">
                        <template>
                            <tbody>
                                <tr>
                                    <th>Term</th>
                                    <td :colspan="building.length">{{ term }}</td>
                                </tr>
                                <tr>
                                    <th>Building&#40;s&#41;</th>
                                    <td v-for="b in building" :key="b">{{ b }}</td>
                                </tr>
                                <tr>
                                    <th :rowspan="priorityLifestyle.length+1">Lifestyles</th>
                                </tr>
                                <tr v-for="life in priorityLifestyle" :key="life">
                                    <td :colspan="building.length">{{ life }}</td>
                                </tr>
                            </tbody>
                        </template>
                    </v-simple-table>
                    <br /><v-divider></v-divider>
                    <DisplayMetaData  :metadata="matchingresult" v-if="showmatchingresult"/>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="#0A7029" class="white--text" @click="downloadCSV" :loading="downloadloading">
                        Download
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-snackbar
            v-model="snackbar"
            :color="snackbarcolor"
            :timeout="timeout"
        >
            {{ text }}
        </v-snackbar>
    </div>
</template>
<script>
import DisplayMetaData from "./DisplayMetaData.vue"
export default {
    components: {
        DisplayMetaData
    },
    props: {
        residenceTerm : {
            required: true
        },
        defaultResidenceTerm: {
            required: true
        },
        permissions: {
            required: true
        }
    },
    data(){
        return {
            host: location.hostname.toString(),
            readonly: true,
            term: "",
            building: ["Alumni Court", "Townhouses", "Monsignor Wemple"],
            priorityLifestyle: ["I expect to get up before 9AM on weekdays", "I usually stay up past midnight", "Any kind of noise distracts me while im studying", 
                                "I like to study with background noise", "I want my room to be a gathering place for friends", "I prefer not to have many visitors in my room", "I plan to maintain a fitness program"],
            matchingloading: false,
            showmatchingresult: false,
            matchingresult: {},
            downloadloading: false,
            snackbar: false,
            snackbarcolor: "",
            text: "",
            timeout: 3500,
        }
    },
    methods: {
        initialize(){
            const that = this;
            if(that.permissions.access == "write"){
                that.readonly = false;
            }else {
                that.readonly = true;
            }

            that.term = that.defaultResidenceTerm.term;
            that.building = ["Alumni Court", "Townhouses", "Monsignor Wemple"];
            that.priorityLifestyle = ["I expect to get up before 9AM on weekdays", "I usually stay up past midnight", "Any kind of noise distracts me while im studying", 
                                    "I like to study with background noise", "I want my room to be a gathering place for friends", "I prefer not to have many visitors in my room", "I plan to maintain a fitness program"];
        },
        matchRoomates(){
            const that = this;
            that.matchingloading = true;
            let api = 'https://'+that.host+'/cgi-bin/res-app-api/controller.api.pl';

            let matchingCriteria = {
                residenceTerm: that.term,
                buildings: that.building,
                lifestyles: that.priorityLifestyle
            }

            const formData = new FormData();
            formData.append("mode", "matcher");
            formData.append("data", JSON.stringify(matchingCriteria));

            fetch(api, {
                method: 'POST',
                body: formData
            })
            .then(function(response){
                return response.json();
            })
            .then(function (data) {
                if(data.result.match(/ok/i)){
                    if(data.hasOwnProperty("metadata")){
                        that.matchingresult = data.metadata;
                    }else {
                        that.matchingresult = {};
                    }
                    that.showmatchingresult = true;
                }else {
                    that.setSnackbar("Warning: "+data.message.toString(), "orange");
                }

                that.matchingloading = false;

            })
            .catch(function (err) {
                that.setSnackbar("Error occurred : "+err.toString(), "red");
                that.matchingloading = false;
            })
        },
        downloadCSV(){
            const that = this;
            that.downloadloading = true;
            let api = 'https://'+that.host+'/cgi-bin/res-app-api/controller.api.pl?downloadReport';

            fetch(api)
                .then(function (response) {
                    return response;
                })
                .then(function () {
                    that.setSnackbar("Matching successful - Download starting", "green");
                    window.open(api);   
                    that.downloadloading = false;
                    that.showmatchingresult = false;
                })
                .catch(function (err) {
                    that.setSnackbar("Error: "+err.toString(), "#E57373"); //light red
                })
        },
        setSnackbar(msg, color){ //Sets Snackbar
            const that = this;
            that.snackbar = true;
            that.snackbarcolor = color;
            that.text = msg;
        }
    },
    created(){
        const that = this;
        that.initialize();
    }
}
</script>